import React, { useState, useEffect, useCallback } from 'react';
import MapView from './MapView';
import BandwidthListDrawer from './BandwidthDrawer';
import NEDrawer from './NetworkElementDrawer';
import Spinner from "../DesignSystem/spinner";

interface MyNetworkMapViewProps {
  networkElements: any[];
  selectedNes: any[];
  bandwidthPorts: any[];
  count: number;
  onBandwidthClick: (neId: string, feId: string) => void;
  onNEClick: (neId: string) => void;
  loading: boolean;
  searchText: string;
  onSearchChange: (e: any) => void;
  onSearchClicked: (e: any) => void;
  listBandwidths: {
    available: any[], consumed: any[], sold: any[], contract: any[], demand: any[], other: any[]
  }
}

const MyNetworkMapView: React.FC<MyNetworkMapViewProps> = ({
  networkElements, selectedNes, bandwidthPorts, count, searchText, loading, listBandwidths, onBandwidthClick, onSearchChange, onSearchClicked, onNEClick,
}) => {
  const [bdDrawerContent, setBDDrawerContent] = useState<'list' | 'details' | ''>('');
  const [bandwidthDetails, setBandwidthDetails] = useState<any>({});
  const [nodeIds, setNodeIds] = useState<any[]>([]);
  const [neNames, setNeNames] = useState<{
    nneName: string,
    fneName: string
  }>({
    nneName: '',
    fneName: '',
  });

  const handleBDDrawerContentChange = useCallback((content: 'list' | 'details', bandwidth: any) => {
    setBDDrawerContent(content);
    setBandwidthDetails(bandwidth);
  }, [setBDDrawerContent, setBandwidthDetails]);

  const handleBandwidthClick = useCallback((neId: string, feId: string, details?: any) => {
    setNodeIds([neId, feId]);
    const nne = networkElements.find((item) => Number(item.id) == Number(neId));
    const fne = networkElements.find((item) => Number(item.id) == Number(feId));
    setNeNames({
      nneName: nne.ne_name,
      fneName: fne.ne_name,
    });
    onBandwidthClick(neId, feId);
    if (details.length === 1 && details[0] && details[0].count === 1) {
      setBDDrawerContent('details');
      setBandwidthDetails(details[0]);
    } else {
      setBDDrawerContent('list');
      // setBandwidthDetails(details);
    }
  }, [setNodeIds, setNeNames, setBDDrawerContent, onBandwidthClick, networkElements]);

  const handleBDDrawerClose = useCallback(() => {
    onSearchChange({ target: { value: '' } });
    setBDDrawerContent('');
  }, [onSearchChange, setBDDrawerContent]);

  const [neDrawerContent, setNEDrawerContent] = useState<any>('');

  const handleNEClick = useCallback((neId: string) => {
    const ne = networkElements.find((item) => Number(item.id) === Number(neId));
    if (ne) {
      setNEDrawerContent(ne);
    } else { setNEDrawerContent(''); }
  }, [setNEDrawerContent, networkElements]);

  const handleNEDrawerClose = useCallback(() => {
    setNEDrawerContent('');
  }, []);

  const [geoCenter, setGeoCenter] = useState<{ latitude: number; longitude: number; zoomFactor: number } | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // ✅ Use `useEffect()` to set `hasInitialized` when `selectedNes` first updates
  useEffect(() => {
    if (!hasInitialized && selectedNes.length > 0) {
      setHasInitialized(true);
    }
  }, [selectedNes, hasInitialized]);

  useEffect(() => {
    const calculateCenter = () => {
      const DEFAULT_CENTER = { latitude: 32.8013, longitude: -96.8195, zoomFactor: 1 / 360 };

      if (!hasInitialized || networkElements.length === 0) {
        console.warn("Waiting for selected nodes to update...");
        return null;
      }

      if (selectedNes.length === 0) {
        console.warn("No selected nodes. Using default center.");
        return DEFAULT_CENTER;
      }

      const getGeographicalCoords = (id: any) => {
        const networkElement = networkElements.find((ele: any) => ele.id === id);
        return networkElement
          ? {
              longitude: parseFloat(networkElement.longitude),
              latitude: parseFloat(networkElement.latitude),
            }
          : null;
      };

      const modifiedNodeList = selectedNes
        .map(getGeographicalCoords)
        .filter((node): node is { latitude: number; longitude: number } => node !== null);

      if (modifiedNodeList.length === 0) {
        console.warn("No valid coordinates found. Using default center.");
        return DEFAULT_CENTER;
      }

      if (modifiedNodeList.length === 1) {
        return {
          latitude: parseFloat(modifiedNodeList[0].latitude.toFixed(4)),
          longitude: parseFloat(modifiedNodeList[0].longitude.toFixed(4)),
          zoomFactor: 1 / 360,
        };
      }

      modifiedNodeList.sort((a, b) => a.longitude - b.longitude);
      
      let maxGap = 0;
      let extremeWestNode = modifiedNodeList[0];
      let extremeEastNode = modifiedNodeList[0];
  
      for (let i = 0; i < modifiedNodeList.length - 1; i++) {
        const gap = modifiedNodeList[i + 1].longitude - modifiedNodeList[i].longitude;
        if (gap > maxGap) {
          maxGap = gap;
          extremeEastNode = modifiedNodeList[i];
          extremeWestNode = modifiedNodeList[i + 1];
        }
      }
  
      const wrapGap = (modifiedNodeList[0].longitude + 360) - modifiedNodeList[modifiedNodeList.length - 1].longitude;
      if (wrapGap > maxGap) {
        maxGap = wrapGap;
        extremeWestNode = modifiedNodeList[0];
        extremeEastNode = modifiedNodeList[modifiedNodeList.length - 1];
      }
  
      let midLongitude = (extremeWestNode.longitude + extremeEastNode.longitude) / 2;
      if (extremeWestNode.longitude > extremeEastNode.longitude) {
        midLongitude = ((extremeWestNode.longitude + 180 + (wrapGap/2)) % 360 - 180);
      }
  
      const avgLatitude = modifiedNodeList.reduce((sum, node) => sum + node.latitude, 0) / modifiedNodeList.length;

        // Find extreme north and south nodes
        let extremeNorthNode = modifiedNodeList[0];
        let extremeSouthNode = modifiedNodeList[0];

        for (let node of modifiedNodeList) {
          if (node.latitude > extremeNorthNode.latitude) extremeNorthNode = node;
          if (node.latitude < extremeSouthNode.latitude) extremeSouthNode = node;
        }

        const latGap = Math.abs(extremeNorthNode.latitude - extremeSouthNode.latitude); // Latitude difference
        const longGap = 360 - maxGap; // Longitude difference

        const maxDistance = Math.max(latGap, longGap); // Use the larger of the two

        let zoomFactorLat = 0.005;
        let zoomFactorLong = 0.005;
        let zoomFactorCalc = 0.005;

        if (selectedNes.length > 1) {
          zoomFactorLat = parseFloat(
            (
              0.005 + (0.5 - 0.005) * 
              Math.pow(Math.max(0, Math.log10(Math.max(latGap, 0.01)) / Math.log10(100)), 1.6 )
            ).toFixed(4)
          );

          zoomFactorLong = parseFloat(
            (
              0.005 + (0.5 - 0.005) * 
              Math.pow(Math.max(0, Math.log10(Math.max(longGap, 0.01)) / Math.log10(100)), 2.4 )
            ).toFixed(4)
          );

          zoomFactorCalc = Math.max(zoomFactorLat, zoomFactorLong);
        }


      return {
        latitude: parseFloat(avgLatitude.toFixed(4)),
        longitude: parseFloat(midLongitude.toFixed(4)),
        zoomFactor: zoomFactorCalc,
      };
    };

    const calculatedCenter = calculateCenter();
    if (calculatedCenter) {
      setGeoCenter(calculatedCenter);
    }
  }, [selectedNes, networkElements, hasInitialized]);

  if (!geoCenter) {
    return <Spinner className="map-loading" size={30} />; // ✅ Render spinner while geoCenter is loading
  }

  return (
    <>
      <BandwidthListDrawer
        loading={loading}
        searchText={searchText}
        onSearchClicked={onSearchClicked}
        bandwidthDetails={bandwidthDetails}
        neNames={neNames}
        open={!!bdDrawerContent}
        drawerContent={bdDrawerContent}
        listBandwidths={listBandwidths}
        onClose={handleBDDrawerClose}
        onDrawerContentChange={handleBDDrawerContentChange}
        count={count}
        onSearchChange={onSearchChange}
      />
      <NEDrawer
        open={!!neDrawerContent}
        onClose={handleNEDrawerClose}
        ne={neDrawerContent}
      />
      <MapView
        onNEClick={handleNEClick}
        onBandwidthClick={handleBandwidthClick}
        networkElements={networkElements}
        geographicCenter={geoCenter} // ✅ Guaranteed to be defined before rendering
        bandwidthPorts={bandwidthPorts}
        nodeIds={nodeIds}
        myNetwork
      />
    </>
  );
};

export default MyNetworkMapView;
